import { TAG_FACETS } from '@/api/taxonomy'
import { TagIcon } from '@/components/icons'
import { Panel, TagLink } from '@/components/ui'
import type { PostTagFacets } from '@/types/ui'

/**
 * 详情页右栏的五栏标签（设计稿 D2 右栏的标签分组）。
 *
 * **前四栏固定渲染**：类型 / 社团 / 平台 / 其他。没有值的那一行也留着，
 * 显示一句灰色的「未标注 xx」——「固定标签」指的就是这个：读者在任何一篇
 * 作品上看到的都是同一张表，缺什么一眼能看出来。按有值才渲染的话，
 * 「这篇没标平台」和「这个站不分平台」在界面上就没有区别了。
 *
 * **「更新时间」是例外**：它不是标签，而是发布者手填的一个可选事实。上面那条
 * 理由对它不成立——读者并不需要知道"这篇没填更新时间"，那不是一个缺失的分类，
 * 只是一件没有发生的事。所以没填时整行不渲染。判据是 `TAG_FACETS` 里有没有
 * 配 `emptyHint`，而不是在这里硬编码 facet id：新增一个可选栏目时不该再改本文件。
 *
 * 每个取值都是链接：类型 / 平台 / 其他落到 `?tag_id=`，社团落到 `?author_name=`，
 * 更新时间落到 `?update_year=`（展示到月、检索到年）。链接由 `toTagFacets`
 * 生成，本组件不拼 URL。
 *
 * 别名不在这里显示：它是这篇内容的另一个名字，不是一个可点的栏目取值。
 * 要看别名的话，详情页标题下那一行有。
 */
export function TagGroups({ facets }: { facets: PostTagFacets }) {
  return (
    <Panel className="space-y-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <TagIcon className="size-4 text-primary" />
        标签
      </p>
      <div className="space-y-3">
        {TAG_FACETS.map((facet) => {
          const chips = facets[facet.id]
          // 可选栏目（没有 emptyHint）没有值时整行不渲染。
          if (chips.length === 0 && !facet.emptyHint) return null
          return (
            <div key={facet.id} className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-base font-medium text-primary">
                <span className="size-1.5 shrink-0 bg-accent" aria-hidden="true" />
                {facet.label}
              </p>
              {chips.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5 pl-3">
                  {chips.map((chip) => (
                    <li key={chip.key} className="flex flex-wrap items-baseline gap-1.5">
                      <TagLink to={chip.to}>{chip.label}</TagLink>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pl-3 text-base text-subtle-foreground">{facet.emptyHint}</p>
              )}
            </div>
          )
        })}
      </div>
    </Panel>
  )
}
